import { ESPLoader, HardReset, type IEspLoaderTerminal, type ROM, type Transport } from "esptool-js";

import { readRevision, targetNameOfChip, type ChipIdentity, type ChipRevision } from "./index.ts";

export { SlipTransport } from "./slip-transport.ts";

/** What the ROM loader is spoken to at, and what ESP-IDF's console runs at. */
export const ROM_BAUD = 115200;

/** The chip classes of esptool-js that read the revision's two numbers. */
type VersionedRom = ROM & {
    getMajorChipVersion?(loader: ESPLoader): Promise<number>;
    getMinorChipVersion?(loader: ESPLoader): Promise<number>;
};

/**
 * Reset the chip on `transport` into its ROM loader, ask which chip it is, and
 * close the transport again. The page and `idfweb chip-id` both connect this way.
 */
export async function detectChip(transport: Transport, terminal?: IEspLoaderTerminal): Promise<ChipIdentity> {
    const loader = new ESPLoader({ transport, baudrate: ROM_BAUD, terminal });
    try {
        await loader.connect();
        return await identifyChip(loader);
    } finally {
        await closeQuietly(transport);
    }
}

/**
 * The identity of the chip a connected loader talks to. Only the chip's name
 * is certain once the loader connected; a detail that fails to read is left out.
 */
export async function identifyChip(loader: ESPLoader): Promise<ChipIdentity> {
    const chip = loader.chip as VersionedRom;
    const target = targetNameOfChip(chip.CHIP_NAME);
    return {
        target,
        name: chip.CHIP_NAME,
        revision: await optional(() => revisionOf(chip, target, loader)),
        // esptool-js ends the description with its own revision, which for some chips lacks the minor number.
        description: await optional(async () =>
            (await chip.getChipDescription(loader)).replace(/ \(revision [^)]*\)$/, ""),
        ),
        features: await optional(() => chip.getChipFeatures(loader)),
        mac: await optional(() => chip.readMac(loader)),
    };
}

async function revisionOf(chip: VersionedRom, target: string, loader: ESPLoader): Promise<ChipRevision | undefined> {
    const own = readRevision(target, async (address) => (await loader.readReg(address)) >>> 0);
    if (own !== undefined) return own;
    if (chip.getMajorChipVersion === undefined || chip.getMinorChipVersion === undefined) return undefined;
    return { major: await chip.getMajorChipVersion(loader), minor: await chip.getMinorChipVersion(loader) };
}

async function optional<T>(read: () => Promise<T | undefined>): Promise<T | undefined> {
    try {
        return await read();
    } catch {
        return undefined;
    }
}

/** Reset into the application with the RTS and DTR lines. */
export async function hardReset(transport: Transport): Promise<void> {
    await transport.setDTR(false);
    await transport.setRTS(true);
    await new HardReset(transport).reset();
}

/** Close the transport, ignoring that the port is closed or gone already. */
export async function closeQuietly(transport: Transport): Promise<void> {
    try {
        await transport.disconnect();
    } catch {
        // Nothing to do about it and nothing waiting on it.
    }
}
